/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * CustomBorderTable Component
 *
 * Renders a customizable table with a sticky header using Material UI components.
 * Supports dynamic columns and row rendering with custom row keys.
 *
 * @param {Object} props - Component properties
 * @param {string} [props.id] - Unique identifier for the table element
 * @param {Array<Object>} [props.rows=[]] - Data array used to populate table rows
 * @param {Array<Object>} [props.columns=[]] - Column definitions
 * @param {string} props.columns[].id - Unique key for the column, used to access row data
 * @param {React.ReactNode} props.columns[].header - Content to display in the column header
 * @param {Function} [props.columns[].render] - Optional custom render function for cell content
 * @param {string} [props.columns[].className] - Optional CSS classes applied to the header and body cells of the column
 * @param {Function} [props.getRowKey] - Function to generate a unique key for each row
 * @param {string} [props.tableClassName=""] - Additional CSS classes for the table container
 *
 * @returns {JSX.Element} Rendered table component
 */

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

const CustomBorderTable = ({
    id,
    rows = [],
    columns = [],
    getRowKey = (row, index) => index,
    tableClassName = "",
}) => {
    return (
        <TableContainer className={`overflow-y-auto ${tableClassName}`}>
            <Table id={id} stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.id} className={`ibl-font-w-600 border-0 background-general-00 ${col.className || ""}`}>
                                {col.header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, index) => {
                        const key = getRowKey(row, index);
                        return (
                            <TableRow key={key}>
                                {columns.map((col) => (
                                    <TableCell key={col.id} className={`${row.className ?? ''} background-general-00 ${col.className || ""}`}>
                                        {col?.render ? col.render(col.id, row, index) : row[col.id]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CustomBorderTable;
